import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { AnimatedSection } from "@/components/AnimatedSection";
import type { GalleryPhoto } from "@/lib/gallery.functions";

interface Props {
  photos: GalleryPhoto[];
}

/**
 * Homepage workshop strip — pulls live "Featured" photos from the gallery DB.
 * Falls back gracefully when fewer than 2 are flagged featured.
 * Uses thumbnail/medium variants via srcset for fast LCP.
 */
export function FeaturedPhotosStrip({ photos }: Props) {
  const fallback = [
    { src: "/gallery/workshop-mercedes-amg.jpg", srcSet: undefined as string | undefined, alt: "Mercedes AMG E63 on the MOT ramp at Woodseer Autos", caption: null as string | null },
    { src: "/gallery/workshop-bentley.jpg", srcSet: undefined, alt: "Bentley Continental GT outside Woodseer Autos", caption: null },
    { src: "/gallery/workshop-rolls-royce.jpg", srcSet: undefined, alt: "Rolls-Royce Ghost in the workshop", caption: null },
    { src: "/gallery/workshop-porsche-ramp.jpg", srcSet: undefined, alt: "Porsche Panamera on the ramp for inspection", caption: null },
  ];

  const items =
    photos.length >= 2
      ? photos.slice(0, 8).map((p) => {
          const thumb = p.variants?.thumb?.url;
          const medium = p.variants?.medium?.url;
          const parts: string[] = [];
          if (thumb && p.variants?.thumb?.width) parts.push(`${thumb} ${p.variants.thumb.width}w`);
          if (medium && p.variants?.medium?.width) parts.push(`${medium} ${p.variants.medium.width}w`);
          if (p.width) parts.push(`${p.public_url} ${p.width}w`);
          return {
            src: thumb || medium || p.public_url,
            srcSet: parts.length ? parts.join(", ") : undefined,
            alt: p.alt_text,
            caption: p.caption,
          };
        })
      : fallback;

  return (
    <AnimatedSection>
      <section className="section-y">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-xl">
              <p className="eyebrow">Inside the workshop</p>
              <h2 className="mt-3 display-lg text-foreground">Real work. Real vehicles.</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Photos straight from our Poplar workshop — daily drivers, family cars, vans and the occasional something special. No stock images.
              </p>
            </div>
            <Link
              to="/gallery"
              className="inline-flex items-center gap-1.5 text-primary font-bold text-sm hover:text-primary/80 transition-colors shrink-0"
            >
              View full gallery <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {items.map((img, i) => (
              <motion.figure
                key={`${img.src}-${i}`}
                initial={{ opacity: 0, scale: 0.97 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="relative overflow-hidden rounded-2xl border border-border aspect-[4/3] group shadow-lg shadow-black/[0.06]"
              >
                <img
                  src={img.src}
                  srcSet={img.srcSet}
                  sizes="(max-width: 768px) 50vw, 25vw"
                  alt={img.alt}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  {(img.caption || img.alt) && (
                    <figcaption className="text-xs text-white font-semibold leading-snug line-clamp-2">
                      {img.caption || img.alt}
                    </figcaption>
                  )}
                </div>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>
    </AnimatedSection>
  );
}
